/**
 * Ordering Packet — manual vendor ordering layer.
 * ─────────────────────────────────────────────────────────────────────────────
 * Sits ON TOP of the existing VendorPO / OrderDraft system. We do not have
 * direct vendor integrations, so a "Send to Ordering" action does NOT place
 * an order — it creates a structured packet for a back-office rep to manually
 * place with the vendor (Sentrel, Cosentino, Home Depot, custom).
 *
 * Lifecycle:
 *   draft → needs_review → ready_to_place → partially_placed → placed
 *                                                ↓
 *                                          backordered / vendor_issue
 *                                                ↓
 *                                       received / cancelled
 *
 * Audit trail: EVERY price edit, vendor change, substitution, status flip,
 * and override is appended to `auditEvents`. Pricing is editable indefinitely
 * after placement (per product owner) — every revision is logged.
 *
 * This model is additive — it does not mutate Job.financials.contractValue,
 * customer pricing, or WarehouseInventoryContext. It is the source of truth
 * for confirmed *actual* COGS once a packet reaches "placed".
 */

export type OrderingPacketStatus =
  | "draft"               // Just sent to ordering by sales/ops
  | "needs_review"        // Has exceptions (missing SKU, no price, vendor unavailable)
  | "ready_to_place"      // Rep has reviewed; ready to call/order
  | "partially_placed"    // Some lines placed, others pending
  | "placed"              // All lines placed with vendor(s)
  | "backordered"         // Vendor confirmed backorder
  | "vendor_issue"        // Pricing dispute, item discontinued, etc.
  | "price_override_pending_review" // Override exceeds threshold; needs leadership approval
  | "received"            // All lines received / intake complete
  | "cancelled";          // Packet voided

export type OrderingLineStatus =
  | "pending"             // Not yet acted on
  | "ordered"             // Placed with vendor
  | "substituted"         // Different SKU / different vendor used
  | "backordered"         // Confirmed backorder
  | "delayed"             // Vendor confirmed late
  | "unavailable"         // Cannot fulfill at all
  | "received"            // Physically received
  | "cancelled";

export type OrderingLineExceptionKind =
  | "no_price"
  | "no_sku"
  | "vendor_unavailable"
  | "discontinued"
  | "price_variance"
  | "substituted"
  | "vendor_changed"
  | "manual_vendor"
  | "backorder";

export interface OrderingLineException {
  kind: OrderingLineExceptionKind;
  detail: string;
  raisedAt: string;
  raisedBy: string;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
}

/**
 * A single line item on a packet. `projected*` is what came from the BOM
 * (the original estimate/COGS basis). `actual*` is what the rep confirms
 * with the vendor — these become the COGS source after placement.
 */
export interface OrderingLineItem {
  id: string;
  // Projected (from BOM, never mutated after creation)
  projectedSku: string;
  projectedItemName: string;
  projectedVendor: string;
  projectedQty: number;
  projectedUnitPrice: number;       // 0 if BOM has no price
  projectedLineCost: number;
  category?: string;

  // Actual (rep-confirmed; nullable until confirmed)
  actualSku?: string;
  actualItemName?: string;
  actualVendor?: string;
  actualQty?: number;
  actualUnitPrice?: number;
  actualLineCost?: number;
  actualTax?: number;
  actualShipping?: number;
  actualFees?: number;

  // Substitution / change tracking
  substituted: boolean;
  substitutionReason?: string;
  vendorChanged: boolean;
  vendorChangeReason?: string;

  // Per-line state
  status: OrderingLineStatus;
  vendorOrderNumber?: string;
  expectedDeliveryDate?: string;     // ISO
  receivedAt?: string;               // ISO
  notes?: string;
  exceptions: OrderingLineException[];

  // Receipt assignment — set when the line is pulled into a VendorReceipt
  // for reconciliation. null/undefined = unreconciled.
  receiptId?: string;

  // PO assignment — set when the line is grouped into a PurchaseOrder
  // (one PO per vendor). null/undefined = not yet on a PO.
  poId?: string;
}

export type OrderingPacketOrderType = "Batch" | "Direct" | "Rush";

export type OrderingPacketPaymentMethod =
  | "Net 30"
  | "Net 15"
  | "COD"
  | "Credit Card"
  | "ACH"
  | "Other";

/**
 * Audit event — append-only record of EVERYTHING that happens to a packet.
 * Used by the (future) Ordering Integrity Review surface.
 */
export type OrderingAuditEventKind =
  | "packet_created"
  | "status_changed"
  | "vendor_changed"
  | "line_substituted"
  | "line_added"
  | "line_removed"
  | "price_edited"
  | "price_override"
  | "qty_edited"
  | "exception_raised"
  | "exception_resolved"
  | "marked_placed"
  | "marked_received"
  | "cancelled"
  | "note_added"
  | "receipt_created"
  | "receipt_updated"
  | "receipt_locked"
  | "receipt_unlocked"
  | "receipt_deleted"
  | "po_created"
  | "po_updated"
  | "po_status_changed"
  | "po_sent"
  | "po_cancelled"
  | "po_invoice_linked"
  | "po_payment_recorded";

export interface OrderingAuditEvent {
  id: string;
  packetId: string;
  lineItemId?: string;
  receiptId?: string;
  poId?: string;
  kind: OrderingAuditEventKind;
  at: string;            // ISO
  actor: string;         // role label or rep name
  summary: string;       // human-readable
  // For price edits / overrides
  oldValue?: string | number;
  newValue?: string | number;
  varianceAmount?: number;     // newValue - oldValue (currency)
  reason?: string;             // required for overrides + substitutions
}

/* ── Vendor receipt reconciliation ──────────────────────────────────────────
 * CFO-grade model: each vendor invoice → one VendorReceipt. Line items are
 * assigned to a receipt (line-id → receipt-id). Fees are categorized into
 * 4 fixed buckets (matches GAAP-style treatment):
 *
 *   • freight    → capitalizable to landed material cost
 *   • tax        → liability / sales-tax-payable account
 *   • processing → period expense (card/processing fees)
 *   • other      → period expense (with required note)
 *
 * Receipt locks at variance = $0.00 (subtotal + fees == invoice total).
 * Locked receipts are immutable until explicitly unlocked.
 */
export type ReceiptFeeBucket = "freight" | "tax" | "processing" | "other";

export const RECEIPT_FEE_BUCKET_LABEL: Record<ReceiptFeeBucket, string> = {
  freight: "Freight / Delivery",
  tax: "Sales Tax",
  processing: "Processing / Card Fee",
  other: "Other",
};

export const RECEIPT_FEE_BUCKET_HINT: Record<ReceiptFeeBucket, string> = {
  freight: "Capitalized to landed material cost",
  tax: "Tracked as sales-tax liability",
  processing: "Period expense — card / processing fees",
  other: "Period expense — note required",
};

export interface VendorReceipt {
  id: string;
  packetId: string;
  vendorName: string;
  invoiceNumber: string;          // vendor's invoice / receipt #
  invoiceDate: string;            // ISO date
  invoiceTotal: number;           // dollar total printed on the invoice (target to match)
  // Fees, categorized
  fees: {
    freight: number;
    tax: number;
    processing: number;
    other: number;
  };
  otherFeeNote?: string;          // required if fees.other > 0
  notes?: string;
  attachmentName?: string;        // file name placeholder (no upload in beta)
  locked: boolean;                // true once variance hits $0.00 and user confirms
  lockedAt?: string;              // ISO
  lockedBy?: string;
  createdAt: string;              // ISO
  createdBy: string;
}


/* ── Purchase Order (PO) ─────────────────────────────────────────────────────
 * The formal company document the bookkeeper/CFO works with. ONE PO per
 * vendor per packet. PO holds the vendor order #, invoice #s, and links
 * to receipts via receiptIds. Lines reference the PO via line.poId.
 *
 * Lifecycle:
 *   draft → sent → acknowledged → partially_received → fully_received
 *                                         ↓
 *                                  invoiced → paid → closed
 *                            (cancelled / disputed as off-ramps)
 *
 * 3-way match (soft): PO subtotal vs sum of receipt invoiceTotals vs sum of
 * receipt reconciled-line costs. Mismatches surface on Variance queue but
 * do NOT block payment marking.
 */
export type PurchaseOrderStatus =
  | "draft"
  | "sent"
  | "acknowledged"
  | "partially_received"
  | "fully_received"
  | "invoiced"
  | "paid"
  | "closed"
  | "cancelled"
  | "disputed";

export const PO_STATUS_LABEL: Record<PurchaseOrderStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  acknowledged: "Acknowledged",
  partially_received: "Partially Received",
  fully_received: "Fully Received",
  invoiced: "Invoiced",
  paid: "Paid",
  closed: "Closed",
  cancelled: "Cancelled",
  disputed: "Disputed",
};

export const PO_STATUS_TONE: Record<PurchaseOrderStatus, "muted" | "info" | "warning" | "success" | "destructive"> = {
  draft: "muted",
  sent: "info",
  acknowledged: "info",
  partially_received: "info",
  fully_received: "success",
  invoiced: "warning",
  paid: "success",
  closed: "muted",
  cancelled: "muted",
  disputed: "destructive",
};

/** A vendor invoice attached to a PO. Links 1:1 to a VendorReceipt when reconciled. */
export interface POInvoiceLink {
  invoiceNumber: string;
  invoiceDate: string;     // ISO
  invoiceAmount: number;
  receiptId?: string;      // matches a VendorReceipt.id when 3-way reconciled
  paid: boolean;
  paidDate?: string;       // ISO
  paidAmount?: number;
  paymentReference?: string; // check #, ACH ref, etc.
}

export interface PurchaseOrder {
  id: string;
  packetId: string;
  jobId: string;
  jobLabel: string;          // denorm for finance hub display
  customerName: string;

  poNumber: string;          // PO-YYYY-MMDD-### (human readable, sequential)
  vendorName: string;        // ONE vendor per PO

  status: PurchaseOrderStatus;
  // Vendor-side references
  vendorOrderNumber?: string;   // their confirmation # ("SO-88421")
  vendorContact?: string;       // free text — name / email / phone
  expectedDeliveryDate?: string;// ISO

  // Money — derived from line.actualLineCost for lines on this PO
  subtotal: number;             // sum of line.actualLineCost (or projected if not yet confirmed)
  // Invoices attached (vendor may bill in stages: deposit + balance)
  invoices: POInvoiceLink[];
  // Receipts (physical goods received) — array of VendorReceipt.id
  receiptIds: string[];

  // Payment terms
  paymentMethod?: OrderingPacketPaymentMethod;
  paymentTerms?: string;

  // Lifecycle timestamps
  createdAt: string;
  createdBy: string;
  sentAt?: string;
  sentBy?: string;
  closedAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;

  notes?: string;
}

/**
 * The packet itself — one per "Send to Ordering" submission.
 * A job can have multiple packets over time (re-orders, change orders, etc.).
 */
export interface OrderingPacket {
  id: string;
  jobId: string;
  jobLabel: string;          // denormalized for display
  customerName: string;

  // Submission context (from Send to Ordering modal)
  intendedVendor: string;    // "Sentrel" | "Cosentino" | ... | "Mixed"
  orderType: OrderingPacketOrderType;
  notes: string;
  sentBy: string;
  sentAt: string;            // ISO

  // Lifecycle
  status: OrderingPacketStatus;
  assignedTo?: string;       // ordering rep
  placedAt?: string;
  placedBy?: string;
  receivedAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;

  // Vendor confirmation
  vendorOrderNumber?: string;        // e.g. Sentrel order #
  paymentMethod?: OrderingPacketPaymentMethod;
  paymentTerms?: string;             // free text
  expectedDeliveryDate?: string;     // packet-wide ETA (rep can override per line)

  // Lines
  lineItems: OrderingLineItem[];

  // Vendor receipts (reconciliation) — added at first receipt creation
  receipts?: VendorReceipt[];

  // Purchase Orders — one per vendor, generated when "Place Order" runs.
  // Lives on the packet for context, also flattened into Finance hub.
  purchaseOrders?: PurchaseOrder[];

  // Totals (derived but cached for queue display)
  projectedTotal: number;
  actualTotal: number;       // 0 until lines are confirmed

  // Audit + exceptions
  auditEvents: OrderingAuditEvent[];
  exceptionCount: number;    // derived count of unresolved exceptions across lines
}

/* ── Vendor lead-time configuration ─────────────────────────────────────── */

export interface VendorLeadTimeConfig {
  vendorName: string;
  leadTimeDays: number;      // calendar days from order placement to delivery
  notes?: string;
  isPrimary: boolean;        // shown first in selectors
}

export const SEED_VENDOR_LEAD_TIMES: VendorLeadTimeConfig[] = [
  { vendorName: "Sentrel",     leadTimeDays: 7,  isPrimary: true,  notes: "Wall systems — standard lead" },
  { vendorName: "Cosentino",   leadTimeDays: 14, isPrimary: true,  notes: "Dekton — fabricated to spec" },
  { vendorName: "Home Depot",  leadTimeDays: 2,  isPrimary: true,  notes: "Local pickup / next-day delivery" },
  { vendorName: "Daltile",     leadTimeDays: 5,  isPrimary: false, notes: "Tile / accent" },
  { vendorName: "Coastal Shower Doors", leadTimeDays: 10, isPrimary: false, notes: "Glass enclosures" },
  { vendorName: "Delta",       leadTimeDays: 4,  isPrimary: false, notes: "Fixtures" },
  { vendorName: "Moen",        leadTimeDays: 4,  isPrimary: false, notes: "Grab bars / accessories" },
  { vendorName: "Schluter",    leadTimeDays: 3,  isPrimary: false, notes: "Waterproofing" },
  { vendorName: "Other",       leadTimeDays: 7,  isPrimary: false, notes: "Default — custom vendor" },
];

/* ── Status display helpers ─────────────────────────────────────────────── */

export const PACKET_STATUS_LABEL: Record<OrderingPacketStatus, string> = {
  draft: "Draft",
  needs_review: "Needs Review",
  ready_to_place: "Ready to Place",
  partially_placed: "Partially Placed",
  placed: "Placed",
  backordered: "Backordered",
  vendor_issue: "Vendor Issue",
  price_override_pending_review: "Override Pending Review",
  received: "Received",
  cancelled: "Cancelled",
};

export const PACKET_STATUS_TONE: Record<OrderingPacketStatus, "muted" | "info" | "warning" | "success" | "destructive"> = {
  draft: "muted",
  needs_review: "warning",
  ready_to_place: "info",
  partially_placed: "info",
  placed: "success",
  backordered: "warning",
  vendor_issue: "destructive",
  price_override_pending_review: "warning",
  received: "success",
  cancelled: "muted",
};

export const LINE_STATUS_LABEL: Record<OrderingLineStatus, string> = {
  pending: "Pending",
  ordered: "Ordered",
  substituted: "Substituted",
  backordered: "Backordered",
  delayed: "Delayed",
  unavailable: "Unavailable",
  received: "Received",
  cancelled: "Cancelled",
};
